'use strict';
'use strict';

import jQuery from 'jquery';
import dialog from 'jquery-ui/ui/widgets/dialog';
import _debounce from 'lodash.debounce';
import _throttle from 'lodash.throttle';
import FB from './../../vendor/facebook';
import ga from './../../vendor/google-analytics';

/**
 * Utility functions used throughout the website
 */
var Utilities = (function($) {
  return function (options) {

    // Namespace for private variables and functions
    var privateNS = {
      defaults: { // Namespace for default options
      }
    };

    // Namespace for public variables and functions
    var publicNS = {};

    /**
     * Construct a new instance
     */
    privateNS.init = function () {

      // Create a holder for install timers
      privateNS.timers = {};

      // Return the public namespace
      return publicNS;
    };

    /**
     * Adds a function to a list that gets executed on each animation frame.
     * These functions are run very frequently and so very little computation
     * should occur inside of them. A handle needs to be supplied so the
     * function can be kept track of, and potentially removed.
     *
     * @param handle string A name that this timer should be reference by
     * @param func function The function to execute on each frame
     *
     * @returns boolean True if a timer was installed for the handle. False
     *                  otherwise.
     */
    publicNS.installTimer = function installTimer(handle, func) {

      // Request for func to be run on the next animation frame and
      // store back the id that the browser returns
      privateNS.timers[handle] = publicNS.requestAnimationFrame(function installedFunction() {

        // If this handle has not yet been cancelled, create a new timer
        // for it and record the id from the browser
        if (typeof privateNS.timers[handle] !== 'undefined') {

          // Call the supplied function
          func();

          // Create the new timer
          publicNS.installTimer(handle, func);
        }
      });
    };

    /**
     * Returns the timer id associated with a given handle
     *
     * @param handle The handle to lookup up the id for
     * @returns int The id for the supplied handle if it exists
     */
    publicNS.getTimerId = function getTimerId(handle) {
      return privateNS.timers[handle];
    };

    /**
     * Cancels and removes a timer from the page that was installed
     * by the supplied handle. Returns true if a timer was actually
     * cancelled. Returns false if no timer with the supplied handle
     * could be found.
     *
     * @param handle string The handle of the timer to remove
     * @returns boolean False if a timer could not be found for the handle,
     *                  true otherwise
     */
    publicNS.uninstallTimer = function uninstallTimer(handle) {

      // If the timer does not exist for the supplied handle then it
      // can not be removed.
      if (typeof privateNS.timers[handle] === 'undefined' || privateNS.timers[handle] === null) {
        return false;
      }

      // Notify the window that this timer should be removed
      publicNS.cancelAnimationFrame(privateNS.timers[handle]);

      // Delete the timer so that upon the next execution it does not
      // get executed and the handle can be reused
      delete privateNS.timers[handle];

      return true;
    };

    /**
     * Creates a popup with a specific url, width, and height.
     *
     * @param url string The url to show in the popup
     * @param width int The width of the popup to show
     * @param height int The height of the popup to show
     *
     * @returns boolean True if a popup was successfully created, false otherwise
     */
    publicNS.popup = function popup(url, width, height) {

      // Fail if the required arguments are not passed
      if (typeof url === 'undefined' || typeof width === 'undefined' || typeof height === 'undefined') {
        return false;
      }

      // Use the supplied offsets if they are passed, otherwise attempt
      // to center it in the users screen. The logic for centering a popup
      // is taken from the Facebook SDK. There is a trickyness to get
      // the browsers to behave when using multiple monitors
      var screenX = window.screenX !== 'undefined' ? window.screenX : window.screenLeft,
        screenY = window.screenY !== 'undefined' ? window.screenY : window.screenTop,
        screenWidth = typeof window.outerWidth !== 'undefined' ? window.outerWidth : document.documentElement.clientWidth,
        screenHeight = typeof window.outerHeight !== 'undefined' ? window.outerHeight : document.documentElement.clientHeight,
        adjustedScreenX = screenX < 0 ? window.screen.width + screenX : screenX,
        leftOffset = parseInt(adjustedScreenX + (screenWidth - width) / 2, 10),
        topOffset = parseInt(screenY + (screenHeight - height) / 2, 10),
        optionsString = 'menubar=no,location=1,toolbar=no,resizable=yes,scrollbars=yes,width=' + width + ',height=' + height + ',top=' + topOffset + ',left=' + leftOffset;

      // Once the position is calculated, open the window
      var popup = window.open(
        url,
        '',
        optionsString
      );

      // Return the status of the popup being created
      return popup !== null;
    };

    /**
     * Handles user interactions with the social icons. Based on the target of
     * the interaction, an appropriate social media dialog share is displayed.
     *
     * @param event
     */
    publicNS.onSocialShare = function onSocialShare(event) {

      // Get the clicked link and the social service it should share to
      // Use $(this) so the link to grabbed, instead of the link content
      var $target = $(this),
          socialService = $target.attr('data-social-service');

      // If this is one of the social services we handle, prevent default
      if (socialService === 'facebook' || socialService === 'twitter') {

        // Prevent the default link behavior
        event.preventDefault();
      }

      if (socialService === 'facebook') {

        // If the service for this target is Facebook, use the Facebook SDK

        // Use the required in Promise to make sure that the script does not try to
        // share until the Facebook SDK has been loaded.
        // If a failure is returned from the SDK loader, use a popup instead
        FB.then(function(FBSDK) {
          var socialURL = $target.attr('data-social-url'),
            fbOptions = {method: 'feed', link: socialURL};

          $(['name', 'picture', 'caption', 'description']).each(function(i, el) {
            var extraVar = $target.attr('data-social-'+el);

            if (typeof extraVar !== 'undefined') {
              fbOptions[el] = extraVar;
            }
          });

          // Display the Facebook share dialog
          FBSDK.ui(fbOptions, function(response) {
            if (typeof response === 'undefined') {
              publicNS.dialog('<div>Your post was not published.</div>');
            } else {
              publicNS.dialog('<div>Your post was published.</div>');
            }
          });
        }).catch(function() {

          // If the Facebook SDK fails to load for any reason, resort to a popup
          publicNS.popup(
            $target.attr('href'),
            500,
            300
          );
        });
      } else if (socialService === 'twitter' && !window.twttr) {

        // If the service for this target is Twitter, simply use a popup
        publicNS.popup(
          $target.attr('href'),
          500,
          300
        );
      }

      ga(
        'send',
        'event',
        $target.attr('data-ga-category') || 'social',
        $target.attr('data-ga-action') || socialService,
        $target.attr('data-ga-label') || window.location.href
      );
    };

    /**
     * Does a jQuery dialog
     *
     * @param string markup
     * @param int width
     * @param boolean showClose
     */
    publicNS.dialog = function dialog(markup, width, showClose) {
      if (typeof width === 'undefined') {
        width = 300; // jQuery UI default
      }

      if (typeof showClose === 'undefined') {
        showClose = true;
      }

      return $(markup).dialog({
        closeText: 'close',
        classes: {
          "ui-dialog": "transparent-titlebar"
        },
        modal: true,
        width: width,
        draggable: false,
        open: function(e, ui) {
          if (!showClose) {
            $(".ui-dialog-titlebar", ui.dialog).hide();
          }
        },
        close: function () {
          $(this).dialog('destroy');
          $(this).remove();
        }
      });
    };

    /**
     * Wraps iframes in a responsive wrapper
     */
    publicNS.wrapIFrame = function wrapIFrame(containers) {

      // Collect all of the iFrames in all of the containers
      [].slice.call(containers).reduce(
        function(iframes, container) {
          return iframes.concat(

            // Use slice here to get from an iterable HTMLCollection to an array
            [].slice.call(container.getElementsByTagName('iframe'))
          );
        },
        []
      ).filter(function removeWrappedIframes(iframe) {
        return iframe.className.search('fluid-wrapped') === -1;
      }).forEach(function iterateIFrames(iframe) {

        // Ignore any iframes that are specifically marked as ignorable
        if (iframe.className.match(/responsive-ignore/) === null &&
            iframe.className.match(/g-survey-frame/) === null) {

          // Loop through the iframe array and apply the responsive wrapper

          // Extract the elements width first and try to account for 100% width
          // responsive elements
          var elmWidth = iframe.width;
          var widthPercent;

          // If the width is a percent width, then find its parent's width and
          // compute a value
          if (widthPercent = elmWidth.match(/(\d+)%/)) {
            elmWidth = iframe.parentNode.getBoundingClientRect().width * (parseInt(widthPercent[1]) / 100);
          }

          // Calculate the video ratio based on the iframe's w/h dimensions
          var elmRatio = iframe.height > 0 ? (iframe.height / elmWidth) * 100 : 56.25;

          // Change the iframe styling so that it scales exactly according to its
          // parents size
          iframe.style.position = 'absolute';
          iframe.style.top = '0';
          iframe.style.left = '0';
          iframe.width = '100%';
          iframe.height = '100%';
          iframe.className += ' fluid-wrapped';

          // Wrap the iframe in a new <div> which will maintain an aspect ratio
          // relative to the width of its parent. The aspect ratio used is the
          // one derived from the settings of the iframe
          var wrap = document.createElement('div');
          wrap.className = 'fluid-vids';
          wrap.style.width = '100%';
          wrap.style.position = 'relative';
          wrap.style.paddingTop = elmRatio + '%';
          wrap.style.overflow = 'auto';
          wrap.style.margin = '0 auto';

          // Add the iframe inside our newly created <div>
          var iframeParent = iframe.parentNode;
          iframeParent.insertBefore(wrap, iframe);
          wrap.appendChild(iframe);
        }
      });
    };

    /**
     * Generates a weak uuid that can be used to uniquely identify objects.
     * The weakness comes from the reliance on the Javascript Math.random
     * function which is not the best for generating random numbers. Other
     * methods should be used to generate stronger ids.
     *
     * @return string A generated uuid
     */
    publicNS.weakUuid = function weakUuid() {
      try {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      } catch (e) {
        return e;
      }
    };

    /**
     * Forces the page to (instantly) scroll itself to the top of the
     * given element or a specific value
     *
     * @param target mixed Either a DOM element to scroll to, or a fixed
     *                     value to scroll to
     */
    publicNS.goToElement = function goToElement(target) {

      // Determine a scroll target based on the passed in argument
      var scrollTarget = 0;

      // If a integer was passed in, use that value directly
      if (!isNaN(target)) {

        scrollTarget = target;
      } else {

        // Otherwise compute a value based on the DOM target
        scrollTarget = $(target).offset().top;
      }

      // Force the browser to the top of the element
      $('html, body').animate({
        scrollTop: scrollTarget
      }, 0);
    };

    /**
     * Formats a string representing a monetary value as a dollar amount with
     * two decimal places represented
     *
     * @param {string} string The value to format
     * @returns {string} The formatted value
     */
    publicNS.formatAsMoney = function formatAsMoney(string) {

      // Transform the string in to a float, and parse the amount into its
      // individual properties
      var amount = parseFloat(string),
        decPlaces = 2,
        decSeparator = '.',
        thouSeparator = ',',
        sign = amount < 0 ? '-' : '',
        intAmount = parseInt(amount = Math.abs(+amount || 0).toFixed(decPlaces), 10) + '',
        thouSeparatorPos = intAmount.length > 3 ? intAmount.length % 3 : 0;

      // Join the individual pieces back together in to a human readable form
      return '' +
        sign +
        (thouSeparatorPos ? intAmount.substr(0, thouSeparatorPos) + thouSeparator : '') +
        intAmount.substr(thouSeparatorPos).replace(/(\d{3})(?=\d)/g, "$1" + thouSeparator) +
        (decPlaces ? decSeparator + Math.abs(amount - intAmount).toFixed(decPlaces).slice(2) : '');
    };

    /**
     * Filters an Object based on its keys. Returns a new object that contains
     * the key value pairs of the source object where the keys of the pairs are
     * also keys in the filter object.
     *
     * @param {Object} obj The object to apply the filter to
     * @param {Object} filterObj The object to use as the key filter
     * @returns {Object} The filtered object
     */
    publicNS.filterObjectKeys = function filterObjectKeys(obj, filterObj) {

      // Create a filter for the keys that should remain
      var keyFilter = filterObj.hasOwnProperty.bind(filterObj);

      // Create a new object to return
      var returnObj = {};

      // Filter the keys of the passed in object, and add any remaining keys to
      // the return obj
      Object.keys(obj).filter(keyFilter).forEach(function(key) {
        returnObj[key] = obj[key];
      });

      // Return the newly constructed key object
      return returnObj;
    };

    /**
     * Strips sensitive data out of the URL and returns a safe URL for GA tracking
     *
     * @param trackingPathname The data attribute for the current page contained in <HEAD>
     * @return string The tracking URL to send along to analytics, etc
     */
    publicNS.buildTrackingURL = function buildTrackingURL(trackingPathname) {

      if (typeof trackingPathname !== 'undefined' && trackingPathname !== '') {
        // If the tracking pathname is set, construct the tracking URL
        return window.location.protocol + '//' + window.location.host + trackingPathname + window.location.hash;
      } else {
        return window.location.href;
      }
    };

    /**
     * Helper method that given data will return a promise that is already
     * resolved with the data
     *
     * @param {*} data Data of any type
     * @returns {Promise.<T>} A successfully resolved promise that holds the
     *                        passed in data
     */
    publicNS.success = function success(data) {
      return $.Deferred().resolve(data).promise();
    };

    /**
     * Helper method that given data will return a promise that is already
     * rejected with the data
     *
     * @param {*} data Data of any type
     * @returns {Promise.<T>} A rejected promise that holds the passed in data
     */
    publicNS.failure = function failure(data) {
      return $.Deferred().reject(data).promise();
    };

    /**
     * Creates a promise that will successfully resolve after the passed in
     * duration. The promise will resolve with the value actual computed
     * duration between when the promise was created and when the promise
     * resolved.
     *
     * @param {number} duration The number of milliseconds that it should take
     *                          for the promise to resolve
     * @returns {Promise.<T>} A promise that resolves after the passed in
     *                        duration
     */
    publicNS.delay = function delay(duration) {

      // Create a deferred, and a timestamp to track the start time
      var deferred = $.Deferred(),
          startTime = Date.now();

      // Create a timeout that will resolve the deferred after a delay
      setTimeout(function() {
        deferred.resolve(Date.now() - startTime);
      }, duration);

      // Return the promise back to the caller to chain off
      return deferred.promise();
    };

    /**
     * Alias to lodash.debounce
     */
    publicNS.debounce = _debounce;

    /**
     * Alias to lodash.throttle
     */
    publicNS.throttle = _throttle;

    /**
     * Alias to requestAnimationFrame polyfill
     *
     * Make sure that the window is bound as the executing context, otherwise
     * it will attempt to execute in the publicNS context
     */
    publicNS.requestAnimationFrame = window.requestAnimationFrame;

    /**
     * Alias to cancelAnimationFrame polyfill
     *
     * Make sure that the window is bound as the executing context, otherwise
     * it will attempt to execute in the publicNS context
     */
    publicNS.cancelAnimationFrame = window.cancelAnimationFrame;

    // Return a new instance
    return privateNS.init();
  };

})(jQuery);

export default new Utilities()